// @ts-nocheck
// Design primitives for the team-lead dashboard, replicating
// ct-admin-dashboard's src/ui.jsx (Panel / Pill / Dot / LiveDot / MiniBar /
// TableShell / Th / Td) on this project's shadcn tokens instead of that
// mock's own CSS variables.
import React from 'react';
import { HAIRLINE } from '@/features/cti/ctiHelpers';

export { HAIRLINE };

export const TONE = {
  good: { text: 'text-success', bg: 'bg-success', soft: 'bg-success/10', ring: 'ring-success/25' },
  call: { text: 'text-primary', bg: 'bg-primary', soft: 'bg-primary/10', ring: 'ring-primary/25' },
  warn: { text: 'text-warning', bg: 'bg-warning', soft: 'bg-warning/10', ring: 'ring-warning/25' },
  crit: { text: 'text-destructive', bg: 'bg-destructive', soft: 'bg-destructive/10', ring: 'ring-destructive/25' },
  off: { text: 'text-muted-foreground', bg: 'bg-muted-foreground/50', soft: 'bg-muted/50', ring: 'ring-muted/25' },
  info: { text: 'text-indigo-700 dark:text-indigo-300', bg: 'bg-indigo-600 dark:bg-indigo-400', soft: 'bg-indigo-50 dark:bg-indigo-400/10', ring: 'ring-indigo-200 dark:ring-indigo-400/20' },
};

export const nf = (n) => Number(n || 0).toLocaleString('en-IN');

export function Panel({ className = '', children, ...rest }) {
  return (
    <div className={`rounded-2xl border bg-card shadow-sm ${HAIRLINE} ${className}`.trim()} {...rest}>
      {children}
    </div>
  );
}

export function LiveDot({ tone = 'good', label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="relative inline-flex h-1.5 w-1.5">
        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${TONE[tone].bg} opacity-70`} />
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${TONE[tone].bg}`} />
      </span>
      {label ? (
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      ) : null}
    </span>
  );
}

export function Dot({ tone = 'off', size = 6, dim = false }) {
  return (
    <span
      className={`inline-block shrink-0 rounded-full ${TONE[tone].bg}`}
      style={{ width: size, height: size, opacity: dim ? 0.5 : 1 }}
    />
  );
}

export function Pill({ tone = 'off', className = '', children }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE[tone].soft} ${TONE[tone].text} ${className}`.trim()}
    >
      {children}
    </span>
  );
}

export function MiniBar({ value, max = 100, tone = 'call', width = 56, showValue }) {
  const pct = Math.max(0, Math.min(100, (Number(value) / (Number(max) || 1)) * 100));
  return (
    <span className="inline-flex items-center gap-2">
      <span className="relative inline-block h-1.5 overflow-hidden rounded-full bg-muted" style={{ width }}>
        <span
          className={`absolute inset-y-0 left-0 rounded-full transition-[width] duration-300 ${TONE[tone].bg}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      {showValue ? <span className="text-[11px] tabular-nums text-muted-foreground">{showValue}</span> : null}
    </span>
  );
}

export function TableShell({ children, className = '', maxH }) {
  return (
    <div className={`ap-noscrollbar overflow-auto ${className}`.trim()} style={maxH ? { maxHeight: maxH } : undefined}>
      <table className="w-full border-collapse text-left">{children}</table>
    </div>
  );
}

export function Th({ children, align = 'left', className = '' }) {
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : '';
  return (
    <th
      className={`sticky top-0 z-10 whitespace-nowrap border-b bg-card px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground ${HAIRLINE} ${alignCls} ${className}`.trim()}
    >
      {children}
    </th>
  );
}

export function Td({ children, align = 'left', className = '' }) {
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : '';
  return (
    <td className={`whitespace-nowrap border-b px-3 py-2 text-[12.5px] text-foreground ${HAIRLINE} ${alignCls} ${className}`.trim()}>
      {children}
    </td>
  );
}

export function EmptyNote({ children }) {
  return (
    <div className="rounded-lg bg-muted/40 px-3 py-6 text-center text-[11.5px] text-muted-foreground">{children}</div>
  );
}
